/**
 * WCAG 2 relative luminance and contrast ratio.
 *
 * **Contrast validation is not a nicety.** Without it a tenant ships an unreadable app and the
 * support load lands on us rather than on them — so the theme editor *warns* on a large-text
 * pair that fails and **refuses to publish** a body-text pair that does (stack §13.2).
 *
 * The arithmetic is the specification's, not an approximation: the per-channel linearization
 * with its 0.03928 knee, and the 0.2126 / 0.7152 / 0.0722 weights. A simple channel average
 * agrees at black and white and is wrong everywhere in between — which is everywhere a real
 * palette lives.
 */

export type Rgb = { r: number; g: number; b: number }

/** Body copy needs 4.5:1; text at 24px (or 18.66px bold) needs 3:1. */
export type TextSize = 'body' | 'large'

const AA_THRESHOLD: Record<TextSize, number> = { body: 4.5, large: 3 }

const SHORT = /^#?[0-9a-f]{3}$/i
const LONG = /^#?[0-9a-f]{6}$/i

/**
 * Parse `#rrggbb` or `#rgb`. **Undefined for anything else — never a default.**
 *
 * Falling back to black would make an invalid color pass every contrast check against a light
 * background, which is the exact opposite of what this file is for.
 */
export function parseHex(value: string): Rgb | undefined {
  const raw = value.startsWith('#') ? value.slice(1) : value

  if (SHORT.test(value)) {
    /**
     * `#abc` is `#aabbcc` — each digit **doubled**, not zero-padded.
     *
     * Padding makes every short hex far too dark, and short hex is exactly what gets pasted
     * out of a brand guide. Read by index rather than by spreading the string: the regex has
     * already guaranteed three ASCII hex digits, and spreading a string iterates code points.
     */
    const double = (index: number) => Number.parseInt(raw[index]! + raw[index]!, 16)
    return { r: double(0), g: double(1), b: double(2) }
  }

  if (!LONG.test(value)) return undefined

  return {
    r: Number.parseInt(raw.slice(0, 2), 16),
    g: Number.parseInt(raw.slice(2, 4), 16),
    b: Number.parseInt(raw.slice(4, 6), 16),
  }
}

/** The per-channel linearization the specification defines, knee and all. */
function channel(value: number): number {
  const scaled = value / 255
  return scaled <= 0.03928 ? scaled / 12.92 : ((scaled + 0.055) / 1.055) ** 2.4
}

export function relativeLuminance(rgb: Rgb): number {
  return 0.2126 * channel(rgb.r) + 0.7152 * channel(rgb.g) + 0.0722 * channel(rgb.b)
}

/**
 * `(lighter + 0.05) / (darker + 0.05)`, so the order of the arguments does not matter.
 *
 * Returns 0 — a ratio no pair can achieve — when either color will not parse, so an
 * unreadable token cannot slip through as a passing check.
 */
export function contrastRatio(a: string, b: string): number {
  const first = parseHex(a)
  const second = parseHex(b)
  if (first === undefined || second === undefined) return 0

  const light = Math.max(relativeLuminance(first), relativeLuminance(second))
  const dark = Math.min(relativeLuminance(first), relativeLuminance(second))
  return (light + 0.05) / (dark + 0.05)
}

export function meetsAA(foreground: string, background: string, size: TextSize): boolean {
  return contrastRatio(foreground, background) >= AA_THRESHOLD[size]
}

export { AA_THRESHOLD }
